/** P2-W09-SYNC-FB01-IMPL: validate the existing wire shape before Mirror construction. */
import {
  availableResult,
  capabilityDefinitionSnapshot,
  unavailableResult,
  unknownResult,
  type CapabilityDefinitionReadResultV1,
  type DefinitionStatus,
} from '../consumer/contract';

function objectWithKeys(
  value: unknown,
  keys: readonly string[],
): asserts value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('Invalid capability definition object');
  }
  const actual = Reflect.ownKeys(value);
  if (actual.length !== keys.length || keys.some((key) => !Object.hasOwn(value, key))) {
    throw new Error('Invalid capability definition fields');
  }
}

function text(value: unknown): string {
  if (typeof value !== 'string') throw new Error('Invalid capability definition text');
  return value;
}

function version(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
    throw new Error('Invalid capability definition version');
  }
  return value;
}

function lifecycle(value: unknown): DefinitionStatus {
  if (value !== 'DRAFT' && value !== 'PUBLISHED' && value !== 'DEACTIVATED') {
    throw new Error('Invalid capability definition lifecycle');
  }
  return value;
}

export function parseCapabilityDefinitionWire(
  value: unknown,
  expectedCapabilityId: string,
): CapabilityDefinitionReadResultV1 {
  objectWithKeys(value, ['status', 'capabilityId', 'snapshot']);
  const capabilityId = text(value.capabilityId);
  if (capabilityId !== expectedCapabilityId) throw new Error('Capability identity mismatch');
  if (value.status === 'UNKNOWN' || value.status === 'UNAVAILABLE') {
    if (value.snapshot !== null) throw new Error('Unexpected capability definition snapshot');
    return value.status === 'UNKNOWN'
      ? unknownResult(capabilityId)
      : unavailableResult(capabilityId);
  }
  if (value.status !== 'AVAILABLE') throw new Error('Invalid capability definition status');
  const snapshot = value.snapshot;
  objectWithKeys(snapshot, [
    'capabilityId',
    'displayName',
    'capabilityType',
    'definitionVersion',
    'definitionDigest',
    'definitionStatus',
    'aggregateVersion',
    'originLabel',
  ]);
  if (snapshot.capabilityId !== capabilityId) throw new Error('Capability identity mismatch');
  return availableResult(
    capabilityId,
    capabilityDefinitionSnapshot({
      capabilityId: text(snapshot.capabilityId),
      displayName: text(snapshot.displayName),
      capabilityType: text(snapshot.capabilityType),
      definitionVersion: version(snapshot.definitionVersion),
      definitionDigest: text(snapshot.definitionDigest),
      definitionStatus: lifecycle(snapshot.definitionStatus),
      aggregateVersion: version(snapshot.aggregateVersion),
      originLabel: text(snapshot.originLabel),
    }),
  );
}
